import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ConfigService } from 'src/config';
import { Line } from '../../shared/models/line';
import { LineStore } from '../store';
import { AppStateService } from './app-state.service';
import * as _ from 'lodash';
import { _DYN_EMIT_LINE_DELIMITED_3 } from '@microsoft/applicationinsights-web/types/__DynamicConstants';
import { firstValueFrom, of, map, catchError } from 'rxjs';
import { LoggingService } from '@modules/shared/services/logging.service';


@Injectable({
    providedIn: 'root'
})
export class LineService {
    private _config = inject(ConfigService);
    private _http = inject(HttpClient);
    private _lineStore = inject(LineStore);
    private _appStateService = inject(AppStateService);
    private _loggingService = inject(LoggingService);


    constructor() {

    }

    public init() {
        this._appStateService.getPropertyState('selectedGarageCodes')
            .subscribe((selectedGarageCodes: string[]) => {
                this.updateLines(selectedGarageCodes);
            }
        );
    }

    public async updateLines(garageCodes: string[]): Promise<void> {
        if (!garageCodes || garageCodes.length === 0) {
            this._lineStore.resetData();
            return;
        }

        const promiseArray: Promise<Line[]>[] =
            _.map(garageCodes, async garageCode => {
                const trafficAreaName = this._appStateService.getTrafficAreaName(garageCode);

                if (!trafficAreaName) {
                    // Don't fetch lines if trafficAreaName is missing since the line.id will be incomplete, causing bugs and douplicates
                    return;
                }

                return this._fetchLines(garageCode, trafficAreaName);

            });

        Promise.all(promiseArray).then((result: Line[][]) => {
            // Filter out any undefined results (from missing traffic area names)
            const validResults = _.filter(result, r => r !== undefined);
            
            if (validResults.length === 0) {
                // No valid results, don't update to avoid clearing existing data
                return;
            }

            const allLines = _.flatten(validResults);
            this._lineStore.update(allLines);
        }).catch((error) => {
            this._loggingService.logException(error);
            this._loggingService.logEvent('LineService.UpdateLinesError', { errorMessage: error.message, garageCount: garageCodes.length });
            // On network or other errors, don't update the store to avoid clearing existing data
            // The UI will continue to show cached data or loading state
        });

    }

    private async _fetchLines(garageCode: string, trafficAreaName: string): Promise<Line[]> {
        const url = this._config.mapApiBaseUrl + `/v1/garages/${garageCode}/routes`;

        try {
            return await firstValueFrom(this._http.get(url)
                .pipe(
                    map((x: any) => x.routes),
                    map(routes => _.map(routes, route => {
                        const line = new Line();
                        line.add(garageCode);
                        line.routeNumber = route['routeName'];
                        line.trafficAreaName = trafficAreaName;
                        return line;
                    })),
                    catchError(error => {
                        // Log the error but don't throw to avoid breaking Promise.all
                        if (error.status === 0) {
                            this._loggingService.logEvent('LineService.NetworkError', { 
                                garageCode, 
                                url,
                                message: 'Network error - API may be temporarily unavailable' 
                            });
                        } else {
                            this._loggingService.logException(error);
                            this._loggingService.logEvent('LineService.FetchError', { 
                                garageCode, 
                                url,
                                status: error.status,
                                statusText: error.statusText 
                            });
                        }
                        return of([]); // Return empty array instead of undefined to avoid breaking the flow
                    })
                ));
        } catch (error) {
            this._loggingService.logException(error as Error);
            this._loggingService.logEvent('LineService.UnexpectedError', { 
                garageCode, 
                url,
                errorMessage: (error as Error).message 
            });
            return []; // Return empty array on any unexpected errors
        }
    }
}
